import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adsAPI, AdRecord, CreateAdPayload } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import Loading from './Loading';
import { supabase } from '../config/supabase';

const AdminAds: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [ads, setAds] = useState<AdRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentAd, setCurrentAd] = useState<Partial<CreateAdPayload>>({
        placement: 'startup',
        isActive: true
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const fetchAds = async () => {
        try {
            setLoading(true);
            const data = await adsAPI.getAll();
            setAds(data);
        } catch (err) {
            console.error('Failed to fetch ads:', err);
            setError(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAds();
    }, []);

    const handleDelete = async (id: string) => {
        if (window.confirm(t('admin.ads.delete_confirm'))) {
            try {
                await adsAPI.delete(id);
                fetchAds();
            } catch (err) {
                console.error('Failed to delete ad:', err);
                alert(t('common.error'));
            }
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        setUploading(true);
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `ads/${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;

            // Try to upload to 'ads' bucket, fallback to 'server-screenshots' or 'public' if needed
            // Ideally 'ads' bucket should exist. If not, maybe 'public'?
            // Let's try 'server-screenshots' as it is known to exist from ServerForm
            const bucketName = 'server-screenshots';

            const { error: uploadError } = await supabase.storage
                .from(bucketName)
                .upload(fileName, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = supabase.storage
                .from(bucketName)
                .getPublicUrl(fileName);

            setCurrentAd(prev => ({ ...prev, imageUrl: publicUrl }));
        } catch (err) {
            console.error('Upload failed:', err);
            alert(t('common.upload_error') || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload = {
                title: currentAd.title!,
                description: currentAd.description,
                imageUrl: currentAd.imageUrl!,
                linkUrl: currentAd.linkUrl,
                placement: currentAd.placement!,
                startDate: currentAd.startDate!,
                endDate: currentAd.endDate!,
                isActive: currentAd.isActive!
            };

            if (isEditing && (currentAd as any).id) {
                await adsAPI.update((currentAd as any).id, payload);
                alert(t('admin.ads.update_success'));
            } else {
                await adsAPI.create(payload as CreateAdPayload);
                alert(t('admin.ads.create_success'));
            }
            setModalOpen(false);
            fetchAds();
        } catch (err) {
            console.error('Failed to save ad:', err);
            alert(t('common.error'));
        }
    };

    const openEditModal = (ad?: AdRecord) => {
        if (ad) {
            setIsEditing(true);
            setCurrentAd({
                ...ad,
                startDate: ad.startDate.split('T')[0],
                endDate: ad.endDate.split('T')[0]
            });
        } else {
            setIsEditing(false);
            const today = new Date();
            const nextWeek = new Date();
            nextWeek.setDate(today.getDate() + 7);

            setCurrentAd({
                placement: 'startup',
                isActive: true,
                startDate: today.toISOString().split('T')[0],
                endDate: nextWeek.toISOString().split('T')[0]
            });
        }
        setModalOpen(true);
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            // reuse upload logic
            // mocking event object or extracting logic
            // Let's duplicate logic for simplicity or extract it.
            setUploading(true);
            try {
                const fileExt = file.name.split('.').pop();
                const fileName = `ads/${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
                const bucketName = 'server-screenshots';

                const { error: uploadError } = await supabase.storage
                    .from(bucketName)
                    .upload(fileName, file);

                if (uploadError) throw uploadError;

                const { data: { publicUrl } } = supabase.storage
                    .from(bucketName)
                    .getPublicUrl(fileName);

                setCurrentAd(prev => ({ ...prev, imageUrl: publicUrl }));
            } catch (err) {
                console.error('Upload failed:', err);
                alert(t('common.upload_error') || 'Upload failed');
            } finally {
                setUploading(false);
            }
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                    {t('admin.ads.title')}
                </h1>
                <div className="flex gap-4">
                    <button
                        onClick={() => openEditModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('admin.ads.add_new')}
                    </button>
                    <button
                        onClick={() => navigate('/admin')}
                        className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        {t('admin.back_to_dashboard')}
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded mb-6">
                    {error}
                </div>
            )}

            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-900/50 text-gray-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">{t('admin.ads.table.title')}</th>
                                <th className="px-6 py-4">{t('admin.ads.table.placement')}</th>
                                <th className="px-6 py-4">{t('admin.ads.table.dates')}</th>
                                <th className="px-6 py-4">{t('admin.ads.table.status')}</th>
                                <th className="px-6 py-4">{t('admin.users.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        <Loading message={t('common.loading')} />
                                    </td>
                                </tr>
                            ) : ads.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                                        {t('server.list.empty_title')}
                                    </td>
                                </tr>
                            ) : (
                                ads.map((ad) => (
                                    <tr key={ad.id} className="hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center">
                                                <img src={ad.imageUrl} alt={ad.title} className="w-10 h-10 rounded object-cover mr-3 bg-gray-700" />
                                                <div className="font-medium text-white">{ad.title}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-300">
                                            {t(`admin.ads.placement.${ad.placement}`)}
                                        </td>
                                        <td className="px-6 py-4 text-gray-300 text-sm">
                                            <div>{new Date(ad.startDate).toLocaleDateString()}</div>
                                            <div className="text-gray-500">{new Date(ad.endDate).toLocaleDateString()}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${ad.isActive ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
                                                {ad.isActive ? t('admin.ads.status.active') : t('admin.ads.status.inactive')}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => openEditModal(ad)}
                                                className="text-blue-400 hover:text-blue-300 mr-3 transition-colors"
                                            >
                                                {t('common.edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(ad.id)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                {t('common.delete')}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit/Create Modal */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-2xl p-8 max-w-lg w-full border border-gray-700 shadow-2xl overflow-y-auto max-h-[90vh]">
                        <h2 className="text-2xl font-bold mb-6 text-white">
                            {isEditing ? t('common.edit') : t('admin.ads.add_new')}
                        </h2>
                        <form onSubmit={handleSave}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-400 mb-1">{t('admin.ads.form.title')}</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentAd.title || ''}
                                        onChange={e => setCurrentAd({ ...currentAd, title: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-400 mb-1">
                                        {t('admin.ads.form.description') || 'Description'}
                                        <span className="text-xs text-gray-500 ml-2">
                                            ({(currentAd.description || '').length}/150)
                                        </span>
                                    </label>
                                    <textarea
                                        value={currentAd.description || ''}
                                        onChange={e => setCurrentAd({ ...currentAd, description: e.target.value.slice(0, 150) })}
                                        rows={3}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>

                                {/* Drag and Drop Image Upload */}
                                <div>
                                    <label className="block text-gray-400 mb-1">{t('admin.ads.form.image_url')}</label>

                                    <div
                                        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${uploading ? 'bg-gray-700 border-gray-500' : 'border-gray-600 hover:border-blue-500 hover:bg-gray-700/50'}`}
                                        onClick={() => !uploading && fileInputRef.current?.click()}
                                        onDrop={!uploading ? handleDrop : undefined}
                                        onDragOver={handleDragOver}
                                    >
                                        {currentAd.imageUrl ? (
                                            <div className="relative group">
                                                <img src={currentAd.imageUrl} alt="Ad Preview" className="max-h-48 mx-auto rounded" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded">
                                                    <span className="text-white font-medium">Click or Drop to Change</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-4">
                                                <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                </svg>
                                                <p className="text-gray-300 font-medium">Click to upload or drag and drop</p>
                                                <p className="text-sm text-gray-500 mt-1">SVG, PNG, JPG or GIF</p>
                                            </div>
                                        )}
                                        {uploading && (
                                            <div className="mt-2 text-blue-400 text-sm font-medium animate-pulse">Uploading...</div>
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                    />

                                    <div className="mt-2">
                                        <label className="text-xs text-gray-500">Or enter URL manually:</label>
                                        <input
                                            type="text"
                                            required
                                            value={currentAd.imageUrl || ''}
                                            onChange={e => setCurrentAd({ ...currentAd, imageUrl: e.target.value })}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500 mt-1"
                                            placeholder="https://..."
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-gray-400 mb-1">{t('admin.ads.form.link_url')}</label>
                                    <input
                                        type="text"
                                        value={currentAd.linkUrl || ''}
                                        onChange={e => setCurrentAd({ ...currentAd, linkUrl: e.target.value })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-400 mb-1">{t('admin.ads.form.placement')}</label>
                                    <select
                                        value={currentAd.placement}
                                        onChange={e => setCurrentAd({ ...currentAd, placement: e.target.value as any })}
                                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                    >
                                        <option value="startup">{t('admin.ads.placement.startup')}</option>
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-gray-400 mb-1">{t('admin.ads.form.start_date')}</label>
                                        <input
                                            type="date"
                                            required
                                            value={currentAd.startDate || ''}
                                            onChange={e => setCurrentAd({ ...currentAd, startDate: e.target.value })}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-400 mb-1">{t('admin.ads.form.end_date')}</label>
                                        <input
                                            type="date"
                                            required
                                            value={currentAd.endDate || ''}
                                            onChange={e => setCurrentAd({ ...currentAd, endDate: e.target.value })}
                                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={currentAd.isActive}
                                        onChange={e => setCurrentAd({ ...currentAd, isActive: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 bg-gray-700 border-gray-600"
                                    />
                                    <label htmlFor="isActive" className="ml-2 text-gray-300">{t('admin.ads.form.is_active')}</label>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end gap-4">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all disabled:bg-gray-600 disabled:cursor-not-allowed"
                                >
                                    {t('common.save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAds;
